import { useTranslations } from '@vocab/react';
import { Alert, Stack, Text, Toggle } from 'braid-design-system';
import React, { useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';

import { EVENT_TYPE_TOGGLE, HIDE_SALARY } from 'src/app/analytics';
import getTrackingAdapter from 'src/app/dataAnalytics';
import {
  SALARY_HIDE,
  TOGGLE_PRESSED,
} from 'src/app/dataAnalytics/schema/constants';
import {
  FEATURE_DATA_ANALYTICS,
  useFeatureToggleData,
} from 'src/app/featureToggle';
import { actionUpdateDisplayPay } from 'src/app/state/modules/formData/displayPay';
import { actionUpdateSalaryText } from 'src/app/state/modules/formData/salaryText';
import { actionUpdateTrackingDisplayPay } from 'src/app/state/modules/tealium/displayPay';
import { sendLinkEvent } from 'src/app/utils/analytics';
import { getSalaryCurrency } from 'src/app/utils/salary/getSalary';
import { constructDefaultSalaryDisplayText } from 'src/app/utils/salary/utils/formatSalary';
import type AppState from 'src/types/appState';

import { usePayType } from '../../hooks/usePayType';

import translations from './.vocab';
import { getPayDisplayApplicationPercentage } from './utils/getPayDisplayApplicationPercentage';

export const PayDisplayToggle = () => {
  const {
    hirerNation,
    currency,
    displayPay,
    jobLocationCountry,
    maxSalary,
    minSalary,
    salaryText,
    tealium,
  } = useSelector(
    (state: AppState) => ({
      hirerNation: state.session.hirerAccount.nation,
      currency: state.formData.salaryCurrency,
      displayPay: state.formData.displayPay,
      jobLocationCountry: state.session.jobLocationCountry,
      maxSalary: state.formData.maxSalary,
      minSalary: state.formData.minSalary,
      salaryText: state.formData.salaryText,
      tealium: state.tealium,
    }),
    shallowEqual,
  );

  const isPageAnalyticsEnabled = useFeatureToggleData(FEATURE_DATA_ANALYTICS);

  const salaryCurrency = getSalaryCurrency({
    formDataCurrency: currency,
    hirerNation,
    jobLocationCountry,
  });

  const { t } = useTranslations(translations);
  const payType = usePayType();

  const dispatch = useDispatch();
  const [inputValue, setInputValue] = useState<boolean>(displayPay);

  const handleChange = (value: boolean) => {
    const newDisplayPay = !value;

    setInputValue(newDisplayPay);
    dispatch(actionUpdateDisplayPay(newDisplayPay));
    dispatch(actionUpdateTrackingDisplayPay(newDisplayPay));

    if (newDisplayPay && !salaryText) {
      const salaryTextDetails = constructDefaultSalaryDisplayText({
        min: minSalary,
        max: maxSalary,
        currency: salaryCurrency,
        payType,
      });

      dispatch(actionUpdateSalaryText(salaryTextDetails));
    }

    if (isPageAnalyticsEnabled) {
      const eventData = {
        eventName: TOGGLE_PRESSED,
        toggleType: SALARY_HIDE,
        toggleState: newDisplayPay ? 'off' : 'on',
      };
      getTrackingAdapter.sendLinkEvent(eventData);
    } else {
      const eventData = {
        eventType: EVENT_TYPE_TOGGLE,
        toggleType: HIDE_SALARY,
        toggleState: newDisplayPay ? 'off' : 'on',
      };
      sendLinkEvent(tealium, eventData);
    }
  };

  // Calculate the application percentage relevant to the country
  // the job is being posted into
  const applicationPercentage = getPayDisplayApplicationPercentage(
    jobLocationCountry || hirerNation || 'au',
  );
  const hasApplicationPercentage =
    typeof applicationPercentage === 'number' && applicationPercentage > 0;
  const payDisplayToggleAlertText = hasApplicationPercentage
    ? t('pay-display-toggle-alert-text-with-percentage', {
        percentage: applicationPercentage.toString(),
      })
    : t('pay-display-toggle-alert-text');

  return (
    <Stack space="small">
      <Toggle
        id="hideSalary"
        label={t('pay-display-toggle-label')}
        on={!inputValue}
        onChange={handleChange}
      />
      {!displayPay && (
        <Alert id="payDisplayToggleAlert" tone="caution">
          <Text>{payDisplayToggleAlertText}</Text>
        </Alert>
      )}
    </Stack>
  );
};
